/*
 * @Description: 业务上报相关
 * @Author: fiveonelei
 * @Date: 2022-02-24 17:31:23
 * @FilePath: /app-monorepo-projects/packages/app-share/src/libs/report/index.ts
 */
import { IS_SSR } from '@/utils/env';
import { IHashMap } from '@/types/data';
import { IBeaconReportConfig } from '@/types/index';
import { loadWslReportAsync } from '@/utils/load-wslib';
import config from './configs/index';
import { getQueryObj } from '@/utils/no-import-meta/url';
import getBrowserObj from '@/utils/no-import-meta/get-browser-obj';

type DefaultCfg = {
  action_object: string;
  video_id: string;
  owner_id: string;
  type: string;
  os?: number;
};

let beacon: any;  // 类型声明在@/types/wsl-report 中，由于@tentcent/wsl-report 无法直接引入，所以这里暂使用 any 类型
// 完善配置
const fixBeaconCfg = ({ eventTags, pageID }: IBeaconReportConfig) => {
  const ua = navigator.userAgent || '';
  const browserInfo = getBrowserObj(ua);

  // 默认添加项
  const defaultCfg: DefaultCfg = {
    action_object: '',
    video_id: '',
    owner_id: '',
    type: '',
  };

  // 如果是鸿蒙系统，则加一个字段 5
  if (browserInfo.os.harmony) {
    defaultCfg.os = 5;
  }

  // 普通上报
  const userAction: IHashMap = {};
  // 处理点击事件默认值
  Object.keys(eventTags.user_action).forEach((key) => {
    const data = eventTags.user_action[key];
    userAction[key] = {
      position: data.position,
      action_id: data.action_id,
      is_pull_pos: 0, // 点击事件加上拉动位，0-非拉动位，1-拉起 app，2-拉起下载；默认值为 0
      ...defaultCfg,
    };
  });

  // 曝光上报
  const userExposure: IHashMap = {};
  // 处理曝光事件默认值
  Object.keys(eventTags.user_exposure).forEach((key) => {
    const data = eventTags.user_exposure[key];
    userExposure[key] = {
      position: data.position,
      ...defaultCfg,
    };
  });

  // 播放上报
  const videoPlay: IHashMap = {};
  // 处理播放事件默认值
  Object.keys(eventTags?.video_play || {}).forEach((key) => {
    const data = eventTags.video_play?.[key];
    videoPlay[key] = {
      event_type: data?.event_type,
      video_id: '',
      owner_id: '',
      duration: 0,
      last_location: 0,
      video_length: 0,
      play_extra: '',
    };
  });

  return {
    pageID,
    eventTags: {
      ...eventTags,
      user_exposure: userExposure,
      user_action: userAction,
      video_play: videoPlay,
    },
  };
};

let initializedPromiseOutter: Promise<void> | undefined;

// 初始化灯塔上报 sdk 的方法
export const initReport = async (): Promise<void> => {
  if (IS_SSR) return;
  if (!initializedPromiseOutter) {
    initializedPromiseOutter = initReportCore();
  }
  await initializedPromiseOutter;
};

/**
 * 执行数据上报
 * @param String key        数据上报key
 * @param Object type       数据上报补充信息
 * @param Number reportType 数据上报类型，action: 点击上报, exposure: 曝光上报
 */
export const report = async (key: string, extraTypeParams: IHashMap = {}) => {
  if (IS_SSR) return;

  // 等待初始化完成
  await initReport();
  const { collectionid: collectionId, themeid: collectionThemeId } = getQueryObj();
  const type = extraTypeParams.type || {};
  type.collection_id = collectionId || '';
  type.collection_theme_id = collectionThemeId || '';

  beacon.report(`${key}`, {
    ...extraTypeParams,
    type: JSON.stringify(type),
  });
};

// 初始化的核心方法
async function initReportCore(): Promise<void> {
  if (IS_SSR) return;

  const wslReport = await loadWslReportAsync();
  if (!wslReport) return;
  beacon = wslReport.beacon;
  const initConfig = fixBeaconCfg(config);
  await beacon.init(initConfig);
};
